import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { ArrowRight, PlaneTakeoff, Languages, TrendingDown, Info } from "lucide-react";
import { useCurrency } from "@/context/CurrencyContext";
import { track, EVENTS } from "@/lib/track";

// Renders the "See continental EU alternatives" callout inside the Cost tab
// of a UK CS/AI/DS/Design bachelor page. Hidden when the backend reports
// `eligible: false` (any non-UK degree, or a UK degree outside the six
// target subjects).
//
// Wire-up: <ContinentalAlternativesCard degreeId={d.id} />
export default function ContinentalAlternativesCard({ degreeId }) {
  const [state, setState] = useState({ loading: true, data: null, error: null });
  const { format } = useCurrency();

  useEffect(() => {
    if (!degreeId) return;
    let cancelled = false;
    setState({ loading: true, data: null, error: null });
    (async () => {
      try {
        const { data } = await api.get(`/degrees/${degreeId}/continental-alternatives`);
        if (cancelled) return;
        setState({ loading: false, data, error: null });
      } catch (e) {
        if (cancelled) return;
        setState({ loading: false, data: null, error: e });
      }
    })();
    return () => { cancelled = true; };
  }, [degreeId]);

  if (state.loading || state.error) return null;
  const data = state.data;
  if (!data || !data.eligible) return null;
  const alts = data.alternatives || [];
  if (alts.length === 0) return null;

  const bestSavings = Math.max(...alts.map((a) => a.total_savings_vs_uk_usd || 0));

  const fireClick = (alt) => track(EVENTS.CONTINENTAL_ALT_CLICK, {
    source_degree: degreeId,
    target_degree: alt.degree_id,
    target_country: alt.country_code,
    annual_savings_usd: alt.annual_savings_vs_uk_usd,
    total_savings_usd: alt.total_savings_vs_uk_usd,
    english_taught: alt.english_taught,
  });

  return (
    <div className="mt-6 border border-[#4C1D95]/25 rounded-lg bg-[#4C1D95]/5 overflow-hidden"
         data-testid="continental-alternatives-card">
      <div className="px-5 pt-5 pb-4 border-b border-[#4C1D95]/15">
        <div className="flex items-center gap-2 label-uppercase text-[#4C1D95]">
          <PlaneTakeoff className="h-3.5 w-3.5" />
          <span>Cost-sensitive? Look across the Channel.</span>
        </div>
        <h3 className="font-editorial text-2xl mt-2 leading-tight">
          Same subject, {bestSavings > 0 ? <>save up to <span className="text-[#4C1D95]">{format(bestSavings)}</span> over the degree</> : "different price tag"}
        </h3>
        <p className="text-sm text-stone-600 mt-1.5 leading-relaxed">
          These continental EU bachelors teach {alts.some((a) => a.english_taught) ? "in English" : "the same subject"}
          {" "}at a fraction of UK international tuition. Editorially verified — warnings included so you see the trade-offs
          (numerus fixus in NL, concours in FR, blocked-account in DE) alongside the savings.
        </p>
      </div>

      <div className="grid md:grid-cols-3 divide-y md:divide-y-0 md:divide-x divide-[#4C1D95]/15">
        {alts.map((alt) => (
          <div key={alt.degree_id}
               data-testid={`continental-alt-${alt.degree_id}`}
               className="p-4 flex flex-col">
            <div className="flex items-center gap-2 label-uppercase !text-[9px] text-stone-500">
              <span>{alt.country}</span>
              {alt.world_rank && (
                <>
                  <span>·</span>
                  <span>QS #{alt.world_rank}</span>
                </>
              )}
            </div>
            <div className="mt-1 font-editorial text-lg leading-snug text-stone-900">
              <Link
                data-testid={`continental-alt-link-${alt.degree_id}`}
                to={`/degree/${alt.degree_id}`}
                onClick={() => fireClick(alt)}
                className="hover:underline underline-offset-4">
                {alt.official_title}
              </Link>
            </div>
            <div className="text-xs text-stone-600 mt-0.5">
              {alt.university_name}{alt.city ? ` · ${alt.city}` : ""}
            </div>

            <div className="mt-3 flex items-center gap-2 flex-wrap">
              {alt.english_taught && (
                <span data-testid={`continental-en-${alt.degree_id}`}
                      className="inline-flex items-center gap-1 text-[10px] font-underground-sc tracking-wider bg-emerald-50 text-emerald-800 border border-emerald-200 rounded-full px-2 py-0.5">
                  <Languages className="h-2.5 w-2.5" /> English-taught
                </span>
              )}
              {typeof alt.offer_rate === "number" && (
                <span className="inline-flex items-center gap-1 text-[10px] font-underground-sc tracking-wider bg-stone-100 text-stone-700 border border-stone-200 rounded-full px-2 py-0.5">
                  {(alt.offer_rate * 100).toFixed(0)}% offer rate
                </span>
              )}
            </div>

            <div className="mt-3 border-t border-stone-200 pt-3 flex-1">
              <div className="flex items-baseline justify-between">
                <span className="text-[10px] label-uppercase text-stone-500">Tuition /yr</span>
                <span className="font-mono text-sm text-stone-900">{format(alt.tuition_annual_usd)}</span>
              </div>
              {typeof alt.annual_savings_vs_uk_usd === "number" && alt.annual_savings_vs_uk_usd > 0 && (
                <div className="flex items-baseline justify-between mt-1">
                  <span className="text-[10px] label-uppercase text-[#4C1D95]">Save vs this UK degree</span>
                  <span className="font-mono text-sm text-[#4C1D95] font-semibold flex items-center gap-1">
                    <TrendingDown className="h-3 w-3" />
                    {format(alt.annual_savings_vs_uk_usd)}/yr
                  </span>
                </div>
              )}
              {typeof alt.total_savings_vs_uk_usd === "number" && alt.total_savings_vs_uk_usd > 0 && (
                <div className="text-[10.5px] text-stone-500 mt-0.5 text-right italic">
                  ≈ {format(alt.total_savings_vs_uk_usd)} across the full degree
                </div>
              )}
            </div>

            {alt.warning_note && (
              <div className="mt-3 text-[10.5px] text-amber-900 bg-amber-50 border border-amber-200 rounded-md px-2 py-1.5 leading-snug flex items-start gap-1"
                   data-testid={`continental-warning-${alt.degree_id}`}>
                <Info className="h-3 w-3 mt-0.5 shrink-0" />
                <span>{alt.warning_note}</span>
              </div>
            )}

            <Link
              to={`/degree/${alt.degree_id}`}
              data-testid={`continental-cta-${alt.degree_id}`}
              onClick={() => fireClick(alt)}
              className="mt-3 inline-flex items-center gap-1 text-xs font-underground-sc tracking-wider text-[#4C1D95] hover:underline">
              See full profile <ArrowRight className="h-3 w-3" />
            </Link>
          </div>
        ))}
      </div>

      <div className="px-5 py-3 border-t border-[#4C1D95]/15 bg-white text-[11px] text-stone-500 leading-snug">
        Comparisons use published international tuition. Living costs (rent, food,
        visa, blocked account) vary by city — check each degree's Cost tab before deciding.
      </div>
    </div>
  );
}
